import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "react-icons-kit";
import { x } from "react-icons-kit/feather/x";
import { basic_server_cloud } from "react-icons-kit/linea/basic_server_cloud";
import Tippy from "@tippyjs/react";
import { HexColorInput, HexColorPicker } from "react-colorful";

// packageFile
import packageFile from "../../../../../package.json";

// backshadow variants
const backVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

// modal variants
const modalVariants = {
  hidden: {
    x: 500,
  },
  visible: {
    x: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const SettingsDrawer = ({
  setSettingsDrawer,
  displaySettings,
  setDisplaySettings,
  handleLanguage,
  data,
  countdown,
  donorFeedTimer,
}) => {
  // t function
  const { t } = useTranslation();

  // close drawer
  const handleCloseDrawer = () => {
    setSettingsDrawer(false);
  };

  // icon size
  const [iconSize, setIconSize] = useState(30);

  useEffect(() => {
    // Function to calculate the responsive font size
    const updateIconSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      // Adjust the size dynamically based on the screen width and height
      setIconSize(Math.max(15, (width + height) * 0.01, 20)); // You can adjust this multiplier
    };

    // Update icon size on window resize
    window.addEventListener("resize", updateIconSize);

    // Call the function once to set initial size
    updateIconSize();

    // Cleanup event listener
    return () => {
      window.removeEventListener("resize", updateIconSize);
    };
  }, []);

  // Handle theme change
  const handleThemeChange = (e) => {
    const value = e.target.value;
    setDisplaySettings((prev) => ({
      ...prev,
      theme: value,
    }));
  };

  // gradients array
  const gradients = [
    { name: "sunset-glow" },
    { name: "cool-sky" },
    { name: "forest-mist" },
    { name: "purple-haze" },
    { name: "ocean-wave" },
    { name: "fiery-mix" },
    { name: "twilight" },
    { name: "sunrise-peach" },
    { name: "mint-fusion" },
    { name: "coral-dream" },
    { name: "midnight-violet" },
    { name: "golden-hour" },
    { name: "aqua-splash" },
    { name: "berry-burst" },
    { name: "blush-cloud" },
    // { name: "lava-lamp" },
    { name: "stars-gradient" },
  ];

  // destructure version from package.json
  const { version } = packageFile;

  return (
    <AnimatePresence>
      <motion.div
        className="settings-backdrop"
        variants={backVariants}
        initial="hidden"
        animate="visible"
        onClick={handleCloseDrawer}
      >
        <motion.div
          className="settings-drawer"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="drawer-header">
            <div>{t("LiveDisplay.display_settings")}</div>
            <div className="close-icon" onClick={handleCloseDrawer}>
              <Icon icon={x} size={iconSize} />
            </div>
          </div>
          <div className="drawer-body">
            {/* version and language */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: "1.5vw",
              }}
            >
              <Tippy content={`Version: ${version}`} zIndex={100001}>
                <span style={{ marginRight: "0.5vw" }}>
                  <Icon icon={basic_server_cloud} size={iconSize} />
                </span>
              </Tippy>
              {displaySettings.language === "en" ? (
                <button
                  type="button"
                  id="fr"
                  onClick={(e) => handleLanguage(e.currentTarget.id)}
                  className="change-lang-btn"
                  style={{ fontSize: "1.1vw" }}
                >
                  française
                </button>
              ) : (
                <button
                  type="button"
                  id="en"
                  onClick={(e) => handleLanguage(e.currentTarget.id)}
                  className="change-lang-btn"
                  style={{ fontSize: "1.1vw" }}
                >
                  English
                </button>
              )}
            </div>

            {/* show progress refresh time*/}
            {data?.showProgress && (
              <p className="refresh-update">
                <span className="refresh-text">
                  {t("LiveDisplay.pledge_refresh")}
                </span>
                <span className="refresh-timer">
                  {String(Math.floor(countdown / 60)).padStart(2, "0")}:
                  {String(countdown % 60).padStart(2, "0")}
                </span>
              </p>
            )}

            {/* show donor feed refresh time*/}
            {data?.showDonorFeed && (
              <p className="refresh-update">
                <span className="refresh-text">
                  {t("LiveDisplay.feed_refresh")}
                </span>
                <span className="refresh-timer">
                  {String(Math.floor(donorFeedTimer / 60)).padStart(2, "0")}:
                  {String(donorFeedTimer % 60).padStart(2, "0")}
                </span>
              </p>
            )}

            {/* select theme */}
            <h5 className="setting-heading">
              {t("LiveDisplay.background_theme")}
            </h5>
            <div className="radio-group">
              {["Color", "Gradient", "Image"]
                .filter(
                  (option) =>
                    option !== "Image" || data.liveDisplayImageName !== null
                )
                .map((option) => (
                  <label key={option} className="radio-label">
                    <input
                      type="radio"
                      name="theme"
                      value={option}
                      checked={displaySettings.theme === option}
                      onChange={handleThemeChange}
                    />
                    {t(`LiveDisplay.${option.toLowerCase()}`)}
                  </label>
                ))}
            </div>

            {/* bg color picker */}
            {displaySettings.theme === "Color" && (
              <div className="bg-picker">
                <h5 className="setting-heading">
                  {t("LiveDisplay.background_color")}
                </h5>
                <HexColorPicker
                  color={displaySettings.bgColor}
                  onChange={(color) => {
                    setDisplaySettings((prev) => ({
                      ...prev,
                      bgColor: color,
                    }));
                  }}
                />
                <HexColorInput
                  color={displaySettings.bgColor}
                  onChange={(color) => {
                    setDisplaySettings((prev) => ({
                      ...prev,
                      bgColor: color,
                    }));
                  }}
                  className="edit-media-inputs"
                  style={{
                    width: "25%",
                    height: "4vh",
                    margin: "0 auto",
                    fontSize: "1vw",
                    textAlign: "center",
                    padding: "1vw",
                  }}
                />
              </div>
            )}

            {/* gradient picker */}
            {displaySettings.theme === "Gradient" && (
              <div className="gradient-picker">
                <h5 className="setting-heading">
                  {t("LiveDisplay.background_gradient")}
                </h5>
                <div className="gradients-wrapper">
                  {gradients.map(({ name }) => (
                    <div
                      key={name}
                      className={`gradient-box ${name}`}
                      style={{
                        border:
                          displaySettings.bgGradient === name
                            ? "0.2vw solid #007c16"
                            : "none",
                      }}
                      onClick={() =>
                        setDisplaySettings((prev) => ({
                          ...prev,
                          bgGradient: name,
                        }))
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* shape picker */}
            <div className="shape-picker">
              <h5 className="setting-heading">
                {t("LiveDisplay.select_shape")}
              </h5>
              <div
                className={`shape-options ${
                  displaySettings.dontDisplayParticles ? "disabled" : ""
                }`}
              >
                {[
                  { name: "Star", shape: "✦" },
                  { name: "Heart", shape: "❤" },
                  { name: "Circle", shape: "●" },
                  { name: "Diamond", shape: "◆" },
                  { name: "Sparkle", shape: "❇" },
                  { name: "Snowflake", shape: "❄" },
                  { name: "Ribbon", shape: "🎗" },
                ].map(({ name, shape }) => {
                  const isSelected = displaySettings.particleShape === shape;
                  return (
                    <label key={shape} className="shape-option">
                      <input
                        type="radio"
                        name="shape"
                        value={shape}
                        checked={isSelected}
                        onChange={() =>
                          setDisplaySettings((prev) => ({
                            ...prev,
                            particleShape: shape,
                          }))
                        }
                        disabled={displaySettings.dontDisplayParticles}
                      />
                      <span
                        className={`shape-visual ${
                          isSelected && displaySettings.dontDisplayParticles
                            ? "grayscale"
                            : ""
                        }`}
                      >
                        {shape}
                      </span>
                      <span className="shape-name">
                        {t(`LiveDisplay.${name.toLowerCase()}`)}
                      </span>
                    </label>
                  );
                })}
              </div>
              {/* dont display particles */}
              <div className="dont-display-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={displaySettings.dontDisplayParticles}
                    onChange={(e) =>
                      setDisplaySettings((prev) => ({
                        ...prev,
                        dontDisplayParticles: e.target.checked,
                      }))
                    }
                  />
                  <span className="checkbox-custom" />
                  <span className="checkbox-label">
                    {t("LiveDisplay.dont_display_particles")}
                  </span>
                </label>
              </div>
            </div>

            {/* animation effect*/}
            <div className="shape-picker">
              <h5 className="setting-heading">
                {t("LiveDisplay.select_background_animation")}
              </h5>
              <div
                className={`shape-options ${
                  displaySettings.dontDisplayAnimation ? "disabled" : ""
                }`}
              >
                {[
                  { value: "floating-balls", label: "floatingballs" },
                  { value: "color-drip", label: "colordrip" },
                  { value: "shooting-stars", label: "shootingstars" },
                ].map(({ value, label }) => {
                  const isSelected = displaySettings.animationStyle === value;
                  return (
                    <label key={value} className="shape-option">
                      <input
                        type="radio"
                        name="animation"
                        value={value}
                        checked={isSelected}
                        onChange={() =>
                          setDisplaySettings((prev) => ({
                            ...prev,
                            animationStyle: value,
                          }))
                        }
                        disabled={displaySettings.dontDisplayAnimation}
                      />
                      <span
                        className={`shape-visual ${
                          isSelected && displaySettings.dontDisplayAnimation
                            ? "grayscale"
                            : ""
                        }`}
                        style={{ fontSize: "1vw" }}
                      >
                        {t(`LiveDisplay.${label}`)}
                      </span>
                    </label>
                  );
                })}
              </div>
              {/* dont display animation */}
              <div className="dont-display-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={displaySettings.dontDisplayAnimation}
                    onChange={(e) =>
                      setDisplaySettings((prev) => ({
                        ...prev,
                        dontDisplayAnimation: e.target.checked,
                      }))
                    }
                  />
                  <span className="checkbox-custom" />
                  <span className="checkbox-label">
                    {t("LiveDisplay.dont_display_animation")}
                  </span>
                </label>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SettingsDrawer;
